import { TextField } from '@mui/material';
import React, { HTMLAttributes, useCallback } from 'react';
import { ControllerRenderProps, FieldError, FieldValues } from 'react-hook-form';
import { AdornableElement } from '../types';
import { fieldHighlightStyle, handlePasteWithPreprocessor, sanitizeUTF88591 } from './utils';

type InputProps = {
    field: ControllerRenderProps<FieldValues, string>;
    label: string;
    helperText?: string;
    highlighted?: boolean;
    highlightedOnlyIfNonEmpty?: boolean;
    error: FieldError | undefined;
    multiline?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    required?: boolean;
    inputTypeProps?: { inputMode?: HTMLAttributes<unknown>['inputMode']; pattern?: string; type?: string };
} & AdornableElement;

export const Input: React.FC<InputProps> = ({
    field,
    label,
    helperText,
    highlighted,
    highlightedOnlyIfNonEmpty,
    error,
    multiline = false,
    disabled = false,
    readOnly = false,
    required = false,
    endAdornment = () => null,
    inputTypeProps = { type: 'text' },
}) => {
    const sanitizeValue = useCallback(
        (value: string) => {
            if (inputTypeProps.pattern && !new RegExp(inputTypeProps.pattern).test(value)) {
                return null;
            }
            let sanitizedValue = value;
            if (!multiline) {
                sanitizedValue = sanitizedValue.replace(/[\n\r]/g, '');
            }
            sanitizedValue = sanitizeUTF88591(sanitizedValue);
            return sanitizedValue;
        },
        [multiline, inputTypeProps.pattern]
    );
    const onChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const sanitized = sanitizeValue(e.target.value);
            if (sanitized === null) {
                // Cancel event
                e.stopPropagation();
                return;
            }
            e.target.value = sanitized;
            return field.onChange(e);
        },
        [sanitizeValue, field.onChange]
    );

    return (
        <TextField
            {...field}
            onChange={onChange}
            inputRef={field.ref}
            inputProps={{
                ...inputTypeProps,
            }}
            ref={null}
            value={field.value ?? ''}
            label={label}
            size="small"
            error={!!error}
            multiline // IMPACT-2333
            helperText={error?.message || helperText}
            disabled={disabled || readOnly}
            required={required}
            fullWidth
            onPaste={handlePasteWithPreprocessor(sanitizeValue, field.onChange)}
            sx={{
                flexGrow: 1,
                ...(highlighted && (!highlightedOnlyIfNonEmpty || !!field.value) ? fieldHighlightStyle : undefined),
            }}
            InputLabelProps={{ shrink: true }}
            InputProps={{
                endAdornment: endAdornment({ field, disabled, readOnly }),
            }}
        />
    );
};
